import { useState } from "react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { Badge } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SignedPdf } from "@/components/SignedPdf";
import { Section, F, btn } from "@/components/CaseBits";
import { MultiPdfPicker, checkPdfs, uploadFiling } from "@/components/MultiPdfPicker";

const STATUS_CLS = {
  filed: "bg-blue-50 text-blue-900", objected: "bg-amber-50 text-amber-900", heard: "bg-slate-100 text-slate-800",
  allowed: "bg-green-50 text-green-900", partly_allowed: "bg-emerald-50 text-emerald-900", dismissed: "bg-red-50 text-red-900",
};
const DECIDED = ["allowed", "partly_allowed", "dismissed"];
const TA = "rounded-none min-h-[88px]";

const ApplyForm = ({ caseId, onDone }) => {
  const blank = { title: "", relief_sought: "", grounds: "", urgent: false };
  const [f, setF] = useState(blank);
  const [files, setFiles] = useState([]);
  const [merge, setMerge] = useState(false);
  const [progress, setProgress] = useState(null);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  return (
    <form data-testid="interim-form" className="grid grid-cols-1 gap-4"
      onSubmit={async (e) => {
        e.preventDefault();
        const bad = checkPdfs(files);
        if (bad) return toast.error(bad);
        try {
          await api.post(`/cases/${caseId}/interim`, f);
          const rows = await uploadFiling(caseId, `S.17 application — ${f.title}`, "application", files, { merge, onProgress: setProgress });
          toast.success(`Section 17 application filed${rows.length ? ` with ${rows.length} PDF(s)` : ""}`);
          setF(blank); setFiles([]); setProgress(null); onDone();
        } catch (err) { toast.error(errMsg(err)); setProgress(null); }
      }}>
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-4 items-end">
        <F label="Application title" required value={f.title} onChange={(v) => set("title", v)} testId="interim-title-input"
          placeholder="Interim injunction restraining encashment of bank guarantee" />
        <label className="flex items-center gap-2 h-10 text-sm">
          <input type="checkbox" checked={f.urgent} onChange={(e) => set("urgent", e.target.checked)} data-testid="interim-urgent-checkbox" /> Urgent
        </label>
      </div>
      <div className="space-y-2"><Label className="label-caps">Interim relief sought (Section 17)</Label>
        <Textarea required value={f.relief_sought} onChange={(e) => set("relief_sought", e.target.value)} className={TA} data-testid="interim-relief-input" /></div>
      <div className="space-y-2"><Label className="label-caps">Grounds</Label>
        <Textarea required value={f.grounds} onChange={(e) => set("grounds", e.target.value)} className={TA} data-testid="interim-grounds-input" /></div>
      <MultiPdfPicker files={files} onChange={setFiles} label="Attach the application, affidavit and annexures (PDF)" testId="interim-pdfs"
        progress={progress} merge={merge} onMergeChange={setMerge} />
      <Button type="submit" className={btn} data-testid="interim-submit-button">File application</Button>
    </form>
  );
};

const ObjectionForm = ({ ia, caseId, onDone }) => {
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [progress, setProgress] = useState(null);
  return (
    <form className="mt-3 flex flex-col gap-2" data-testid={`objection-form-${ia.id}`}
      onSubmit={async (e) => {
        e.preventDefault();
        const bad = checkPdfs(files);
        if (bad) return toast.error(bad);
        try {
          await api.post(`/interim/${ia.id}/objection`, { text });
          const rows = await uploadFiling(caseId, `Objections to ${ia.title}`, "application", files, { onProgress: setProgress });
          toast.success(`Objections filed${rows.length ? ` with ${rows.length} PDF(s)` : ""}`);
          setText(""); setFiles([]); setProgress(null); onDone();
        } catch (err) { toast.error(errMsg(err)); setProgress(null); }
      }}>
      <Textarea required value={text} onChange={(e) => setText(e.target.value)} placeholder="Objections to the application…" className={TA} data-testid={`objection-input-${ia.id}`} />
      <MultiPdfPicker files={files} onChange={setFiles} label="Attach the reply / objections and annexures (PDF)" testId={`objection-pdfs-${ia.id}`} progress={progress} />
      <Button type="submit" variant="outline" className="rounded-none uppercase tracking-widest text-xs h-9 self-start" data-testid={`objection-submit-${ia.id}`}>File objections</Button>
    </form>
  );
};

const OrderForm = ({ ia, onDone }) => {
  const [f, setF] = useState({ decision: "allowed", order_text: "" });
  return (
    <form className="mt-3 grid grid-cols-1 sm:grid-cols-[180px_1fr_auto] gap-3 items-end border border-blue-900/30 bg-blue-50 dark:bg-blue-950/30 p-3" data-testid={`interim-order-form-${ia.id}`}
      onSubmit={async (e) => {
        e.preventDefault();
        try { await api.post(`/interim/${ia.id}/order`, f); toast.success("Interim order passed — parties notified"); onDone(); }
        catch (err) { toast.error(errMsg(err)); }
      }}>
      <div className="space-y-2"><Label className="label-caps">Decision</Label>
        <Select value={f.decision} onValueChange={(v) => setF((s) => ({ ...s, decision: v }))}>
          <SelectTrigger className="rounded-none h-10 bg-background" data-testid={`interim-decision-${ia.id}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            {[["allowed", "Allowed"], ["partly_allowed", "Partly allowed"], ["dismissed", "Dismissed"], ["heard", "Heard — reserved"]].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
          </SelectContent>
        </Select></div>
      <div className="space-y-2"><Label className="label-caps">Interim order</Label>
        <Textarea required value={f.order_text} onChange={(e) => setF((s) => ({ ...s, order_text: e.target.value }))} className={`${TA} bg-background`} data-testid={`interim-order-text-${ia.id}`} /></div>
      <Button type="submit" className={btn} data-testid={`interim-order-submit-${ia.id}`}>Pass order</Button>
    </form>
  );
};

export const InterimApplications = ({ caseId, items, kase, isArb, mySide, onReload }) => (
  <Section title="Section 17 — interim applications" testId="interim-section"
    form={!isArb && kase.my_side_paid ? <ApplyForm caseId={caseId} onDone={onReload} /> : !isArb && (
      <p className="text-xs text-muted-foreground" data-testid="interim-locked">Applications may be filed once your side's half of the case fee is paid.</p>
    )}>
    {items.length === 0 ? (
      <p className="px-6 py-10 text-sm text-muted-foreground" data-testid="interim-empty">No interim applications on record.</p>
    ) : items.map((ia) => (
      <div key={ia.id} className="px-6 py-5 border-b border-border last:border-0" data-testid={`interim-row-${ia.id}`}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-sm font-medium flex items-center gap-2 flex-wrap">
              <span className="font-mono text-xs text-blue-900 dark:text-blue-400">{ia.number}</span>
              {ia.title}
              {ia.urgent && <Badge className="bg-red-50 text-red-900">Urgent</Badge>}
            </p>
            <p className="text-xs text-muted-foreground mt-1">Filed by {ia.filed_by_name} ({ia.side}) · {fmtDateTime(ia.created_at)}</p>
          </div>
          <Badge className={STATUS_CLS[ia.status]} data-testid={`interim-status-${ia.id}`}>{ia.status.replace("_", " ")}</Badge>
        </div>
        <dl className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div><dt className="label-caps">Relief sought</dt><dd className="mt-1 whitespace-pre-wrap">{ia.relief_sought}</dd></div>
          <div><dt className="label-caps">Grounds</dt><dd className="mt-1 whitespace-pre-wrap">{ia.grounds}</dd></div>
        </dl>
        {ia.objections.map((o, i) => (
          <div key={i} className="mt-3 border-l-2 border-amber-500 pl-3 text-sm" data-testid={`objection-${ia.id}-${i}`}>
            <p className="label-caps">Objections · {o.by_name} ({o.side}) · {fmtDateTime(o.at)}</p>
            <p className="mt-1 whitespace-pre-wrap">{o.text}</p>
          </div>
        ))}
        {ia.order_text && (
          <div className="mt-3 border-l-2 border-blue-900 pl-3 text-sm" data-testid={`interim-order-${ia.id}`}>
            <p className="label-caps">Interim order · {fmtDateTime(ia.decided_at)}</p>
            <p className="mt-1 whitespace-pre-wrap">{ia.order_text}</p>
          </div>
        )}
        <div className="mt-3 flex flex-wrap items-center gap-3">
          {(ia.order_text || isArb) && (
            <SignedPdf kind="interim" targetId={ia.id} documentId={kase.downloads_allowed ? ia.order_document_id : null}
              canUpload={isArb} onDone={onReload} testId={`interim-pdf-${ia.id}`} label="signed interim order" />
          )}
        </div>
        {!isArb && mySide && mySide !== ia.side && !DECIDED.includes(ia.status) && kase.my_side_paid && <ObjectionForm ia={ia} caseId={caseId} onDone={onReload} />}
        {isArb && !DECIDED.includes(ia.status) && <OrderForm ia={ia} onDone={onReload} />}
      </div>
    ))}
  </Section>
);
